import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { CaseTask, TaskPriority } from '~/types/coordinate';

const ASSIGNEES = ['Sarah Johnson', 'Michael Chen', 'Jennifer Martinez', 'Me'];

const DEMO_TODAY = new Date('2026-05-01');

export function relativeDueDate(dueDate: string): { label: string; color: string } {
  const due = new Date(dueDate + 'T00:00:00');
  const days = Math.round((due.getTime() - DEMO_TODAY.getTime()) / 86_400_000);
  if (days < 0) return { label: `Overdue ${Math.abs(days)}d`, color: '#b71c1c' };
  if (days === 0) return { label: 'Due today', color: '#e65100' };
  if (days === 1) return { label: 'Due tomorrow', color: '#e65100' };
  if (days <= 3) return { label: `Due in ${days}d`, color: '#f57f17' };
  return { label: `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, color: '#9e9e9e' };
}

interface Props {
  tasks: CaseTask[];
  onAddTask: (task: Omit<CaseTask, 'id' | 'createdAt'>) => void;
  onUpdateTask: (taskId: string, updates: Partial<CaseTask>) => void;
  highlightedTaskIds?: string[];
}

const PRIORITY_STYLE: Record<TaskPriority, { label: string; bgcolor: string; color: string }> = {
  low:    { label: 'Low',    bgcolor: '#f6f6f6', color: '#668098' },
  normal: { label: 'Normal', bgcolor: '#e5f6fb', color: '#153873' },
  high:   { label: 'High',   bgcolor: '#ffebee', color: '#b71c1c' },
};

export const AddTaskDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (task: Omit<CaseTask, 'id' | 'createdAt'>) => void;
  prefillTitle?: string;
}> = ({ open, onClose, onSave, prefillTitle }) => {
  const [title, setTitle] = useState(prefillTitle ?? '');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [dueDate, setDueDate] = useState('');
  const [assignee, setAssignee] = useState(ASSIGNEES[0]);

  React.useEffect(() => {
    if (open) setTitle(prefillTitle ?? '');
  }, [open, prefillTitle]);

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      status: 'open',
      priority,
      dueDate: dueDate || undefined,
      assignee,
      isSystemGenerated: false,
    });
    setTitle('');
    setPriority('normal');
    setDueDate('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Task</DialogTitle>
      <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>Task</Typography>
          <TextField
            fullWidth value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus size="small"
          />
        </Box>
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>Priority</Typography>
            <Select fullWidth size="small" value={priority} onChange={e => setPriority(e.target.value as TaskPriority)}>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>Due date</Typography>
            <TextField
              fullWidth size="small" type="date"
              value={dueDate} onChange={e => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Stack>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>Assignee</Typography>
          <Select fullWidth size="small" value={assignee} onChange={e => setAssignee(e.target.value)}>
            {ASSIGNEES.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </Select>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!title.trim()}>Add</Button>
      </DialogActions>
    </Dialog>
  );
};

const TaskRow: React.FC<{
  task: CaseTask;
  onToggle: (taskId: string) => void;
  highlighted?: boolean;
  getRef?: (el: HTMLDivElement | null) => void;
}> = ({ task, onToggle, highlighted, getRef }) => {
  const isDone = task.status === 'done';
  const priorityStyle = PRIORITY_STYLE[task.priority];
  const due = task.dueDate ? relativeDueDate(task.dueDate) : null;

  if (task.isSystemGenerated) {
    const createdLabel = new Date(task.createdAt).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    const scheduledLabel = task.dueDate
      ? new Date(task.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : null;

    const highlightSx = highlighted ? {
      borderRadius: 1, px: 0.5, mx: -0.5,
      '@keyframes taskHighlight': {
        '0%': { backgroundColor: '#e5f6fb' },
        '100%': { backgroundColor: 'transparent' },
      },
      animation: 'taskHighlight 5s ease-out forwards',
    } : {};

    return (
      <Box ref={getRef} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.75, opacity: isDone ? 0.45 : 1, ...highlightSx }}>
        <Checkbox
          size="small"
          checked={isDone}
          onChange={() => onToggle(task.id)}
          sx={{ mt: -0.25, p: 0.5, color: '#635f99', '&.Mui-checked': { color: '#00bda1' } }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.82rem',
                textDecoration: isDone ? 'line-through' : 'none',
                color: isDone ? 'text.disabled' : 'text.primary',
              }}
            >
              {task.title}
            </Typography>
            <AutoAwesomeIcon sx={{ fontSize: 12, color: isDone ? 'text.disabled' : '#635f99', flexShrink: 0 }} />
          </Stack>
          <Stack direction="row" spacing={1.25} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
            {scheduledLabel && (
              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                Scheduled {scheduledLabel}
              </Typography>
            )}
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
              Created {createdLabel}
            </Typography>
          </Stack>
        </Box>
      </Box>
    );
  }

  const highlightSx = highlighted ? {
    borderRadius: 1, px: 0.5, mx: -0.5,
    '@keyframes taskHighlight': {
      '0%': { backgroundColor: '#e5f6fb' },
      '100%': { backgroundColor: 'transparent' },
    },
    animation: 'taskHighlight 5s ease-out forwards',
  } : {};

  return (
    <Box ref={getRef} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.75, opacity: isDone ? 0.5 : 1, ...highlightSx }}>
      <Checkbox
        size="small"
        checked={isDone}
        onChange={() => onToggle(task.id)}
        sx={{ mt: -0.25, p: 0.5 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.82rem',
            textDecoration: isDone ? 'line-through' : 'none',
            color: isDone ? 'text.disabled' : 'text.primary',
          }}
        >
          {task.title}
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
          <Chip
            label={priorityStyle.label}
            size="small"
            sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: priorityStyle.bgcolor, color: priorityStyle.color }}
          />
          {due && (
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: due.color, fontWeight: due.color !== '#9e9e9e' ? 700 : 400 }}>
              {due.label}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
            {task.assignee}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

const TaskGroup: React.FC<{
  label: string;
  tasks: CaseTask[];
  onToggle: (id: string) => void;
  emptyText: string;
  highlightedTaskIds?: string[];
  rowRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}> = ({ label, tasks, onToggle, emptyText, highlightedTaskIds, rowRefs }) => {
  const open = tasks.filter(t => t.status !== 'done');
  const done = tasks.filter(t => t.status === 'done');
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        sx={{
          fontSize: '0.8rem', fontWeight: 500,
          textTransform: 'uppercase', letterSpacing: '0.02857em',
          color: 'rgba(0,0,0,0.6)',
          pb: 0.75, mb: 0.5,
          borderBottom: 1, borderColor: 'divider',
        }}
      >
        {label}
      </Typography>
      {open.length === 0 && done.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 1.25, fontSize: '0.82rem' }}>
          {emptyText}
        </Typography>
      ) : (
        <>
          {open.map(task => (
            <TaskRow
              key={task.id}
              task={task}
              onToggle={onToggle}
              highlighted={highlightedTaskIds?.includes(task.id)}
              getRef={el => { rowRefs.current[task.id] = el; }}
            />
          ))}
          {done.length > 0 && (
            <>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1, mb: 0.25, fontSize: '0.68rem' }}>
                Completed
              </Typography>
              {done.map(task => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onToggle={onToggle}
                  highlighted={highlightedTaskIds?.includes(task.id)}
                  getRef={el => { rowRefs.current[task.id] = el; }}
                />
              ))}
            </>
          )}
        </>
      )}
    </Box>
  );
};

const CaseTaskPanel: React.FC<Props> = ({ tasks, onAddTask, onUpdateTask, highlightedTaskIds }) => {
  const [addOpen, setAddOpen] = useState(false);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!highlightedTaskIds?.length) return;
    const el = rowRefs.current[highlightedTaskIds[0]];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedTaskIds]);

  const handleToggle = (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    onUpdateTask(taskId, { status: task.status === 'done' ? 'open' : 'done' });
  };

  const milestoneTasks = tasks.filter(t => t.milestoneKey);
  const aiTasks = tasks.filter(t => t.isSystemGenerated && !t.milestoneKey);
  const manualTasks = tasks.filter(t => !t.isSystemGenerated);
  const openCount = tasks.filter(t => t.status !== 'done').length;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" fontWeight={700}>Tasks</Typography>
          {openCount > 0 && (
            <Chip label={openCount} size="small" sx={{ height: 18, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#153873', color: '#fff' }} />
          )}
        </Stack>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => setAddOpen(true)}
          sx={{ fontSize: '0.75rem' }}
        >
          Add Task
        </Button>
      </Stack>

      {tasks.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>
          No tasks yet
        </Typography>
      ) : (
        <>
          <TaskGroup
            label="Case Checklist"
            tasks={milestoneTasks}
            onToggle={handleToggle}
            emptyText=""
            highlightedTaskIds={highlightedTaskIds}
            rowRefs={rowRefs}
          />
          {aiTasks.length > 0 && (
            <TaskGroup
              label="Automated"
              tasks={aiTasks}
              onToggle={handleToggle}
              emptyText=""
              highlightedTaskIds={highlightedTaskIds}
              rowRefs={rowRefs}
            />
          )}
          {manualTasks.length > 0 && (
            <TaskGroup
              label="Manual"
              tasks={manualTasks}
              onToggle={handleToggle}
              emptyText=""
              highlightedTaskIds={highlightedTaskIds}
              rowRefs={rowRefs}
            />
          )}
        </>
      )}

      <AddTaskDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={task => onAddTask(task)}
      />
    </Box>
  );
};

export default CaseTaskPanel;
